import { ApiErrorData, ErrorWithResponse, Rfc9110Error } from 'types/api'

/**
 * Unified error message extraction for store operations.
 * Handles Axios-style errors, RFC 9110 Problem Details, and common API shapes
 * so notifications and error actions get a single display-ready string.
 *
 * Scenarios covered across the store:
 * - Axios: error.response.data (string or object with detail/message/title/error)
 * - RFC 9110 Problem Details: { title, status } → "Title (status)"
 * - API payloads: detail, message, error (string)
 * - Standard Error and object with .message
 * - Fallback to String(error) when safe
 *
 * Usage: const message = getReadableErrorMessage(caughtError, 'Fallback for user')
 */

// --- Helpers (used by getReadableErrorMessage) ---

/** Non-empty trimmed string from unknown, or null. */
const trimmedString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null
  const str = value.trim()
  return str === '' ? null : str
}

const messageFromRfc = (rfc: Rfc9110Error): string => {
  const title = rfc.title ?? rfc.detail ?? 'Error'
  return rfc.status != null ? `${title} (${rfc.status})` : title
}

const messageFromApi = (api: ApiErrorData): string | null => {
  const rawMessage = api.detail ?? api.message ?? api.error ?? api.title
  const trimmedMessage = trimmedString(rawMessage)
  if (trimmedMessage) return trimmedMessage
  if (api.title != null && api.status != null)
    return `${api.title} (${api.status})`
  return null
}

const lastResortString = (error: unknown): string | null => {
  try {
    const stringValue = String(error)
    return stringValue && stringValue !== '[object Object]' ? stringValue : null
  } catch {
    return null
  }
}

const isErrorWithResponse = (value: unknown): value is ErrorWithResponse =>
  typeof value === 'object' &&
  value !== null &&
  'response' in value &&
  typeof (value as ErrorWithResponse).response === 'object'

const dataAsRfc9110 = (data: unknown): Rfc9110Error | null => {
  if (!data || typeof data !== 'object') return null
  const dataRecord = data as Record<string, unknown>
  if ('title' in dataRecord && 'status' in dataRecord) {
    return {
      title:
        typeof dataRecord.title === 'string' ? dataRecord.title : undefined,
      status:
        typeof dataRecord.status === 'number' ? dataRecord.status : undefined,
      detail:
        typeof dataRecord.detail === 'string' ? dataRecord.detail : undefined,
    }
  }
  return null
}

const dataAsApiError = (data: unknown): ApiErrorData | null => {
  if (!data || typeof data !== 'object') return null
  const dataRecord = data as Record<string, unknown>
  if (
    'detail' in dataRecord ||
    'message' in dataRecord ||
    'title' in dataRecord ||
    'status' in dataRecord ||
    'error' in dataRecord
  ) {
    return {
      detail:
        typeof dataRecord.detail === 'string' ? dataRecord.detail : undefined,
      message:
        typeof dataRecord.message === 'string' ? dataRecord.message : undefined,
      title:
        typeof dataRecord.title === 'string' ? dataRecord.title : undefined,
      status:
        typeof dataRecord.status === 'number' ? dataRecord.status : undefined,
      error:
        typeof dataRecord.error === 'string' ? dataRecord.error : undefined,
    }
  }
  return null
}

/**
 * Extract a single, user-facing error message from a caught value.
 * Use this in store operations for notifications and error actions.
 *
 * Handles:
 * - Axios-style: error.response.data (string or object with detail/message/title/error)
 * - RFC 9110: { title, status } → "Title (status)"
 * - Plain string response.data
 * - Error.message and error.toString()
 *
 * @param error - Caught value (unknown)
 * @param fallback - Message when nothing can be extracted
 * @returns A string safe for UI (notifications, error state)
 */
export const getReadableErrorMessage = (
  error: unknown,
  fallback: string,
): string => {
  if (error == null) return fallback

  // Axios-style: response.data (string, RFC 9110, or API shape)
  if (isErrorWithResponse(error)) {
    const responseData = error.response?.data
    if (responseData != null) {
      const dataAsString = trimmedString(responseData)
      if (dataAsString) return dataAsString
      const rfcError = dataAsRfc9110(responseData)
      if (rfcError) return messageFromRfc(rfcError)
      const apiError = dataAsApiError(responseData)
      if (apiError) {
        const apiMessage = messageFromApi(apiError)
        if (apiMessage) return apiMessage
      }
    }
    const responseErrorMessage = trimmedString(error.message)
    if (responseErrorMessage) return responseErrorMessage
  }

  // Standard Error
  if (error instanceof Error) {
    const errorMessage = trimmedString(error.message)
    if (errorMessage) return errorMessage
  }

  // Object with .message (e.g. some SDKs)
  if (typeof error === 'object' && error !== null && 'message' in error) {
    const messageFromObject = trimmedString(
      (error as { message?: unknown }).message,
    )
    if (messageFromObject) return messageFromObject
  }

  return lastResortString(error) ?? fallback
}
